'use client';
import { ChevronDownSVG, ChevronUpSVG } from '@snack-uikit/icons';
import { Droplist, ItemProps } from '@snack-uikit/list';
import { Typography } from '@snack-uikit/typography';
import cn from 'classnames';
import { useMemo, useState } from 'react';

import { Condition } from '@/components/static';
import { Fn } from '@/types/common';
import { complement, curry, equals } from '@/utils/common';
import { reorderPlatforms } from '@/utils/platform';

import { SelectedContent } from './SelectedContent';
import s from './styles.module.scss';
import { makePlatformMultiselectOptions } from './utils';

type PlatformMultiSelectProps = {
  initSelectedOptions: string[];
  onSelect: Fn<string[], unknown>;
  outerTriggerClassName?: string;
  metaPlatforms: string[];
  isLoadingOptions: boolean;
  lastVisibleSelectedItemIndex?: number;
};

const addOrRemoveOption = curry<[string, string[]], string[]>((option, options) => {
  const isNotCurrentOption = complement(equals(option));
  if (options.includes(option)) return options.filter(isNotCurrentOption);
  return [...options, option];
});

export function PlatformMultiSelect({
  initSelectedOptions,
  onSelect,
  outerTriggerClassName,
  metaPlatforms,
  isLoadingOptions,
  lastVisibleSelectedItemIndex = 1,
}: PlatformMultiSelectProps) {
  const [opened, setOpened] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(initSelectedOptions);
  const Chevron = opened ? ChevronUpSVG : ChevronDownSVG;

  const options: ItemProps[] = useMemo(
    () =>
      makePlatformMultiselectOptions(metaPlatforms.sort(reorderPlatforms)).map(({ name, Icon }) => ({
        beforeContent: <Icon className={s.platform_option_color} />,
        checked: selectedOptions.includes(name),
        onClick() {
          setSelectedOptions(addOrRemoveOption(name));
        },
        content: {
          option: name,
        },
      })),
    [metaPlatforms, selectedOptions, setSelectedOptions],
  );

  const handleOpenChange = (isOpen: boolean) => {
    setOpened(isOpen);
    if (!isOpen) {
      onSelect(selectedOptions);
    }
  };

  return (
    <Droplist
      loading={isLoadingOptions}
      items={options}
      open={opened}
      size='l'
      triggerClassName={outerTriggerClassName}
      selection={{ mode: 'multiple' }}
      onOpenChange={handleOpenChange}
    >
      <div className={cn(s.root, { [s.rootFocus]: opened })} role='button'>
        <div className={s.all_selected_options_wrapper}>
          <Condition when={selectedOptions.length > 1}>
            <Typography.SansBodyS className={s.selected_option_label}>Облачная платформа</Typography.SansBodyS>
          </Condition>

          <SelectedContent
            lastVisibleSelectedItemIndex={lastVisibleSelectedItemIndex}
            metaPlatforms={metaPlatforms}
            selectedOptions={selectedOptions.sort(reorderPlatforms)}
          />
        </div>
        <Chevron />
      </div>
    </Droplist>
  );
}
